import type { LocationPage } from "./types"

/**
 * Local authority pages. These are genuine local pages grounded in verifiable
 * facts from the repository (founders educated at Maastricht University,
 * De Limburger based in Maastricht, Burgerkracht operating in Limburg/Sittard).
 *
 * Rule: never claim leadership or rankings ("leading AI bureau in X") that we
 * cannot support. New city pages may only be added when a meaningfully
 * distinct, factually supported page is possible.
 */
export const locations: LocationPage[] = [
  {
    id: "maastricht",
    pageType: "location",
    slug: { nl: "ai-bureau-maastricht", en: "ai-agency-maastricht" },
    title: {
      nl: "AI Bedrijf Maastricht | AI Bureau voor Maatwerk AI & Automatisering",
      en: "AI Agency Maastricht | Custom AI & Automation",
    },
    description: {
      nl: "AI bedrijf en AI bureau in Maastricht voor maatwerk AI-development, automatisering en AI-agents. Opgericht door Data Science & AI-alumni van de Universiteit Maastricht.",
      en: "AI agency in Maastricht for custom AI development, automation and AI agents. Founded by Data Science & AI alumni of Maastricht University.",
    },
    h1: {
      nl: "AI Bureau Maastricht",
      en: "AI Agency Maastricht",
    },
    cityOrRegion: "Maastricht",
    intro: {
      nl: "Schmeitz De Groen AI Labs bouwt maatwerk AI-systemen vanuit Maastricht. Beide oprichters studeerden Data Science & AI aan de Universiteit Maastricht en begonnen hier hun samenwerking — eerst met freelance projecten, later als officieel AI-ontwikkelbedrijf. Vanuit deze stad werken we voor organisaties in heel Nederland en Limburg, waaronder een toonaangevende regionale uitgeverij uit Maastricht.",
      en: "Schmeitz De Groen AI Labs builds custom AI systems from Maastricht. Both founders studied Data Science & AI at Maastricht University and started their collaboration here — first with freelance projects, later as an official AI development company. From this city we work with organisations across the Netherlands and Limburg, including a leading regional publisher based in Maastricht.",
    },
    localContext: [
      {
        title: { nl: "Geworteld in de Maastrichtse AI-opleiding", en: "Rooted in Maastricht's AI education" },
        body: {
          nl: "Ons technische fundament komt uit de Data Science & AI-opleiding van de Universiteit Maastricht. Geen zelfbenoemde goeroes, maar opgeleide ingenieurs die de fundamenten beheersen en direct toepassen in de praktijk.",
          en: "Our technical foundation comes from the Data Science & AI programme at Maastricht University. Not self-proclaimed gurus, but trained engineers who master the fundamentals and apply them directly in practice.",
        },
      },
      {
        title: { nl: "Werk in en rond Maastricht", en: "Work in and around Maastricht" },
        body: {
          nl: "Voor een toonaangevende regionale uitgeverij uit Maastricht automatiseerden wij de dagelijkse contentdistributie over tientallen doelgroepen. Daarnaast bouwden wij een conversationele navigatieassistent voor een maatschappelijk platform in Limburg.",
          en: "For a leading regional publisher based in Maastricht we automated daily content distribution across dozens of audiences. We also built a conversational navigation assistant for a social platform in Limburg.",
        },
      },
      {
        title: { nl: "Korte lijnen, snelle schakeling", en: "Short lines, fast decisions" },
        body: {
          nl: "Organisaties in de regio werken met ons omdat er geen bureaucratische lagen tussen zitten: je spreekt direct met de ingenieurs die bouwen. Soms adviseren we zelfs om (nog) niet met AI te starten — eerlijkheid gaat voor omzet.",
          en: "Organisations in the region work with us because there are no bureaucratic layers in between: you speak directly with the engineers who build. Sometimes we even advise not to start with AI (yet) — honesty before revenue.",
        },
      },
    ],
    localProofCaseIds: ["de-limburger", "burgerkracht"],
    relevantServiceIds: ["ai-development", "ai-automation", "ai-agents", "ai-knowledge-systems", "ai-workshops", "ai-consultancy"],
  },
  {
    id: "limburg",
    pageType: "location",
    slug: { nl: "ai-bureau-limburg", en: "ai-agency-limburg" },
    title: {
      nl: "AI Bedrijf Limburg | AI Bureau voor Maatwerk AI & Automatisering",
      en: "AI Company Limburg | AI Agency for Custom AI & Automation",
    },
    description: {
      nl: "AI bedrijf en AI bureau in Limburg: maatwerk AI-development, automatisering en kennisbanken. Werkzaam voor organisaties zoals De Limburger en Burgerkracht.",
      en: "AI company and agency in Limburg: custom AI development, automation and knowledge systems. Working with organisations such as De Limburger and Burgerkracht.",
    },
    h1: {
      nl: "AI Bedrijf & AI Bureau Limburg",
      en: "AI Company & AI Agency Limburg",
    },
    cityOrRegion: "Limburg",
    intro: {
      nl: "Als AI-bedrijf vanuit Maastricht ontwikkelen wij AI-oplossingen voor organisaties in heel Limburg. Onze ervaring in de provincie is concreet: voor De Limburger, het grootste regionale dagblad van Limburg, automateerden wij contentdistributie; voor Burgerkracht, de maatschappelijke organisatie voor Limburgers in Sittard, bouwden wij een intelligente navigatieassistent.",
      en: "From Maastricht we develop AI solutions for organisations across Limburg. Our track record in the province is concrete: for De Limburger, Limburg's largest regional daily, we automated content distribution; for Burgerkracht, the social organisation for Limburg's residents in Sittard, we built an intelligent navigation assistant.",
    },
    localContext: [
      {
        title: { nl: "Provinciale organisaties als klant", en: "Provincial organisations as clients" },
        body: {
          nl: "Onze Limburgse projecten spreken voor zich: een geautomatiseerde publicatieworkflow bij de grootste regionale krant van de provincie en een RAG-gebaseerde assistent voor een platform dat Limburgers helpt bij het vinden van passende ondersteuning.",
          en: "Our Limburg projects speak for themselves: an automated publication workflow at the province's largest regional newspaper and a RAG-based assistant for a platform helping Limburg residents find appropriate support.",
        },
      },
      {
        title: { nl: "Bereikbaar in heel Limburg", en: "Serving all of Limburg" },
        body: {
          nl: "Van Maastricht tot Venlo en van Sittard tot Heerlen: we werken zowel op locatie als volledig remote. Veel werk kan digitaal, maar persoonlijk contact beschouwen we als vanzelfsprekend in de regio.",
          en: "From Maastricht to Venlo and from Sittard to Heerlen: we work both on location and fully remote. Much of the work can be digital, but personal contact comes naturally to us in this region.",
        },
      },
      {
        title: { nl: "AI-workshops voor Limburgse bedrijven", en: "AI workshops for Limburg businesses" },
        body: {
          nl: "Naast development verzorgen we workshops en presentaties voor teams in de provincie die AI willen begrijpen en toepassen — van geletterdheid tot strategische planning.",
          en: "Alongside development we deliver workshops and presentations for teams across the province who want to understand and apply AI — from literacy to strategic planning.",
        },
      },
    ],
    localProofCaseIds: ["de-limburger", "burgerkracht"],
    relevantServiceIds: ["ai-development", "ai-automation", "ai-knowledge-systems", "ai-workshops", "ai-consultancy"],
  },
]

export function getLocation(id: string): LocationPage | undefined {
  return locations.find((l) => l.id === id)
}
